'use client';

import { fetchApi } from '@/requests/api';
import {
  buildRetryWithMotivation,
  parsedErrorNeedsMotivation,
  requestUserMotivation,
} from '@/requests/api/handle-motivation';
import type { BaseInstance, ModelName } from '@pigello/pigello-matrix';

import { ObjectKeys, raise } from '@/lib/utils';
import { useSession } from '@/providers/session-provider';
import { BASE_BACKEND_URL } from '@/requests/constants';
import {
  toExternalFieldNames,
  toInternalFieldNames,
} from '@/requests/instanceMapper';
import type { RequestData } from '@/requests/types';
import { useQueryClient } from '@tanstack/react-query';
import { set } from 'es-toolkit/compat';
import { useCallback, useRef, useState } from 'react';
import { useConfig } from './useConfig';

export default function useBulk<Instance extends BaseInstance>({
  modelName,
  disableRetryChunk = false,
}: {
  modelName: ModelName;
  disableRetryChunk?: boolean;
}) {
  const qc = useQueryClient();
  const { config } = useConfig<Instance>(modelName);
  const [total, setTotal] = useState(0);
  const [savedCount, setSavedCount] = useState(0);
  const [didSave, setDidSave] = useState(false);
  const [loading, setLoading] = useState(false);

  const [totalError, setTotalError] = useState(false);
  const {
    data: {
      session: { organizationId },
    },
  } = useSession();
  const reset = () => {
    setTotal(0);
    setDidSave(false);
    setSavedCount(0);
    setLoading(false);
  };

  const invalidateQueries = useCallback(() => {
    qc?.invalidateQueries({
      queryKey: [modelName],
    });
    qc?.invalidateQueries({
      queryKey: [modelName, 'allinstances'],
    });
    qc?.invalidateQueries({
      queryKey: [modelName, 'list'],
    });
  }, [modelName, qc]);

  const motivationPromiseRef = useRef<Promise<string | null> | null>(null);

  const getSharedMotivation = useCallback(async (): Promise<string | null> => {
    if (!motivationPromiseRef.current) {
      motivationPromiseRef.current = requestUserMotivation();
    }

    return motivationPromiseRef.current;
  }, []);

  const saveChunk = useCallback(
    async (
      chunk: Partial<Instance>[] = [],
      alreadySavedInstances: Partial<Instance>[] = [],
      totalFailedInstances: Partial<Instance>[] = [],
      getMotivation?: () => Promise<string | null>
    ): Promise<{ saved: Partial<Instance>[]; failed: Partial<Instance>[] }> => {
      const nextChunk: Partial<undefined | Instance>[] = [...chunk];
      const errored = [...totalFailedInstances];
      const saved = [...alreadySavedInstances];

      const listUrl = `${BASE_BACKEND_URL}/${config?.listUrl}/`;
      const chunkBody = JSON.stringify(chunk);

      const res = await fetchApi({
        method: 'POST',
        url: listUrl,
        body: chunkBody,
      });

      const json = (await res.json()) as Instance[];

      if (res.status === 400) {
        const requestData: RequestData = {
          type: 'POST',
          url: listUrl,
          options: { body: chunkBody },
        };

        if (parsedErrorNeedsMotivation(json)) {
          const motivation = getMotivation ? await getMotivation() : null;
          const next =
            motivation !== null
              ? buildRetryWithMotivation(requestData, motivation)
              : undefined;

          if (next) {
            try {
              const result = (await next()) as { data: unknown };
              const dataArray = result.data;

              if (!Array.isArray(dataArray)) {
                raise('Unexpected bulk response');
              }

              setSavedCount((prev) => prev + dataArray.length);

              const newInstances: Partial<Instance>[] = [];
              for (const instanceData of dataArray) {
                if (config) {
                  newInstances.push(
                    toInternalFieldNames<Instance>(
                      config,
                      instanceData as Instance
                    )
                  );
                }
              }

              return { saved: [...saved, ...newInstances], failed: errored };
            } catch {
              return {
                saved,
                failed: [...errored, ...chunk],
              };
            }
          }

          return {
            saved,
            failed: [...errored, ...chunk],
          };
        }

        if (!Array.isArray(json)) {
          return {
            saved,
            failed: [...errored, ...chunk],
          };
        }

        for (let i = 0; i < json.length; i++) {
          const instanceAttempt = json[i];

          if (ObjectKeys(instanceAttempt).length > 0) {
            const errorInstance = nextChunk.splice(i, 1, undefined);

            if (errorInstance[0]) {
              errored.push(errorInstance[0]);
            }
          }
        }

        if (nextChunk.length === 0 || disableRetryChunk) {
          return { saved, failed: errored };
        }

        return await saveChunk(
          nextChunk.filter((c) => c !== undefined) as Partial<Instance>[],
          saved,
          errored,
          getMotivation
        );
      } else if (res.status === 201) {
        setSavedCount((prev) => prev + json.length);

        const newInstances: Partial<Instance>[] = [];
        for (const instanceData of json) {
          if (config) {
            newInstances.push(
              toInternalFieldNames<Instance>(config, instanceData)
            );
          }
        }

        return { saved: [...saved, ...newInstances], failed: errored };
      } else {
        raise('Unknown Error');
      }
    },
    [disableRetryChunk, config]
  );

  const postInstances = useCallback(
    async (chunks: Partial<Instance>[][], parallel = true) => {
      if (!config) return;

      if (parallel) {
        const promises = chunks.map((c) =>
          saveChunk(c, [], [], getSharedMotivation)
        );

        const res = await Promise.allSettled(promises);

        let succeededInstances: Partial<Instance>[] = [];
        let failedInstances: Partial<Instance>[] = [];

        for (const cur of res) {
          if (cur.status === 'fulfilled') {
            succeededInstances = [...succeededInstances, ...cur.value.saved];
            failedInstances = [...failedInstances, ...cur.value.failed];
          } else {
            setTotalError(true);
          }
        }

        if (!succeededInstances.length) {
          setTotalError(true);
        }

        return { succeededInstances, failedInstances };
      } else {
        const totalRes = {
          succeededInstances: [] as Partial<Instance>[],
          failedInstances: [] as Partial<Instance>[],
        };

        for (const chunk of chunks) {
          const partRes = await saveChunk(chunk, [], [], getSharedMotivation);

          totalRes.succeededInstances.push(...partRes.saved);
          totalRes.failedInstances.push(...partRes.failed);
        }

        if (!totalRes.succeededInstances.length) {
          setTotalError(true);
        }

        return totalRes;
      }
    },
    [saveChunk, getSharedMotivation, config]
  );

  const save = useCallback(
    async ({
      chunkSize = 500,
      instances,
      parallel = true,
    }: {
      chunkSize?: number;
      parallel?: boolean;
      instances: Partial<Instance>[];
    }) => {
      if (!config) return;
      try {
        setLoading(true);

        motivationPromiseRef.current = null;

        const snakedInstances: Partial<Instance>[] = instances.map((i) =>
          toExternalFieldNames(config, i)
        );

        snakedInstances.forEach((instance) => {
          if (!instance.id) {
            set(instance, 'organization.id', organizationId);
          }
        });

        const chunks: Partial<Instance>[][] = [];
        for (let i = 0; i < snakedInstances.length; i += chunkSize) {
          chunks.push(snakedInstances.slice(i, i + chunkSize));
        }

        setTotal(snakedInstances.length);

        const res = await postInstances(chunks, parallel);

        if (!res) {
          invalidateQueries();
          setLoading(false);
          setTotalError(true);
          setDidSave(false);
          setSavedCount(0);
          return {
            error: true,
            didSave: false,
            succeededInstances: [],
            failedInstances: [],
          };
        }

        const { succeededInstances, failedInstances } = res;
        const hasSavedAny = succeededInstances.length > 0;
        setLoading(false);
        setDidSave(hasSavedAny);

        invalidateQueries();

        return {
          succeededInstances,
          failedInstances,
          didSave: hasSavedAny,
          error: instances.length > 0 && !hasSavedAny,
        };
      } catch {
        invalidateQueries();

        setLoading(false);
        return {
          succeededInstances: [],
          failedInstances: [],
          didSave: false,
          error: true,
        };
      }
    },
    [postInstances, organizationId, invalidateQueries, config]
  );

  return {
    reset,
    loading,
    hasError: totalError,
    save,
    didSave,
    progress: total > 0 ? Math.round((savedCount / total) * 100) : 0,
  };
}
